import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import useStyles from '../../../assets/styles/DQProV2LoginStyles';
import PoweredByDQ_Default from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import { loginUser, getGoogleSignInLink, loginUserViaToken, persistGuestLoginUser, persistGuestGetGoogleSignInLink, selectOrgService } from '../../../services/auth.service';
import { useAuth } from '../../../context/auth';
import { save_e2ee_keys } from '../../../utils/utils';
import { LOCAL_STORAGE_KEYS, USER_ACCESS_LEVEL } from '../../../utils/constants';
import LoaderWithBackDrop from '../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import OrgPickerDialog from '../../../components/OrgPickerDialog/OrgPickerDialog.comp';
import { clearAllBrandingCaches } from '../../../context/branding/branding.context';

const getPostLoginRedirect = (user, userMessage) => {
    if (user?.access_level === USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL) {
        return ['/dq_pro/individual-dashboard/score-overview', { userMessage }];
    }
    return ['/my_workspace', { userMessage }];
};

const DQProV2Login = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const history = useHistory();
    const location = useLocation();
    const { setAuthToken, setAuthUser, authUser, authToken } = useAuth();

    // Persist userMessage in localStorage to survive Google redirect
    const [userMessage, setUserMessage] = useState(location.state?.userMessage || localStorage.getItem(LOCAL_STORAGE_KEYS.dqpro_user_message) || '');

    useEffect(() => {
        if (location.state?.userMessage) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_user_message, location.state.userMessage);
            setUserMessage(location.state.userMessage);
        }
    }, [location.state?.userMessage]);

    // Redirect already-authenticated (non-guest) users away from the login page
    useEffect(() => {
        if (authToken && authUser && !authUser.is_guest) {
            history.replace(...getPostLoginRedirect(authUser, userMessage));
        }
    }, [authToken, authUser]); // eslint-disable-line react-hooks/exhaustive-deps

    console.log('[DQProV2Login] userMessage:', userMessage);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    // Confirmation dialog shown when a guest session tries to log in
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    // Tracks which action triggered the warning dialog: 'email' | 'google'
    const [pendingGuestAction, setPendingGuestAction] = useState(null);

    // Multi-org picker state
    const [orgPickerOpen, setOrgPickerOpen] = useState(false);
    const [orgPickerOrgs, setOrgPickerOrgs] = useState([]);
    const [preAuthToken, setPreAuthToken] = useState(null);
    const [orgSelecting, setOrgSelecting] = useState(false);
    const [orgTokenExpired, setOrgTokenExpired] = useState(false);

    /** Shared helper — persists auth data and navigates after a successful login response */
    const handleAuthSuccess = async (data) => {
        clearAllBrandingCaches();
        setAuthToken(data.auth_token);
        setAuthUser(data.user);
        localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(data.refresh_token));
        localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(data.user.email));
        if (data.e2ee_pub_key) save_e2ee_keys({ data });
        history.push(...getPostLoginRedirect(data.user, userMessage));
    };

    const loginViaToken = async (token) => {
        try {
            setLoading(true);
            const result = await loginUserViaToken(token, false);

            // v1.2 — Google OTP callback can also trigger org picker
            if (result.requires_org_selection) {
                setPreAuthToken(result.data.pre_auth_token);
                setOrgPickerOrgs(result.data.orgs || []);
                setOrgTokenExpired(false);
                setOrgPickerOpen(true);
                return;
            }

            if (result.data && result.data.user) {
                await handleAuthSuccess(result.data);
            }
        } catch (e) {
            // error already toasted by invokeApi
        } finally {
            setLoading(false);
        }
    };

    const loginWithGoogleAction = async () => {
        // Guest session: show warning before proceeding (merging into existing Google account)
        if (authUser?.is_guest) {
            setPendingGuestAction('google');
            setShowConfirmDialog(true);
            return;
        }
        setLoading(true);
        try {
            const result = await getGoogleSignInLink();
            const { redirect_link } = result.data;
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect, 'true');
            window.open(redirect_link, '_self');
            // Do NOT setLoading(false) — page navigates away
        } catch (e) {
            // error already toasted by invokeApi
            setLoading(false);
        }
    };

    // Executes the guest persist Google sign-in after the warning is confirmed
    const handleGuestGoogleSignIn = async () => {
        setLoading(true);
        try {
            // persistGuestGetGoogleSignInLink returns only { redirect_link }
            const result = await persistGuestGetGoogleSignInLink();
            const { redirect_link } = result.data;
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect, 'true');
            window.open(redirect_link, '_self');
            // Do NOT setLoading(false) — page navigates away
        } catch (e) {
            // error already toasted by invokeApi
            setLoading(false);
        }
    };

    // Handle Google OAuth2 redirect callback on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const access_token = params.get('otp_token');
        if (access_token) {
            loginViaToken(access_token).then(r => console.log('Logged in via Google callback token'));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleContinue = async () => {
        if (!email.trim() || !password.trim()) return;

        // Guest session: intercept with a warning dialog before touching the API
        if (authUser?.is_guest) {
            setPendingGuestAction('email');
            setShowConfirmDialog(true);
            return;
        }

        // Regular login
        try {
            setLoading(true);
            const loginResult = await loginUser(email.trim(), password, false);

            // Multi-org branch: show workspace picker
            if (loginResult.requires_org_selection) {
                setPreAuthToken(loginResult.data.pre_auth_token);
                setOrgPickerOrgs(loginResult.data.orgs || []);
                setOrgTokenExpired(false);
                setOrgPickerOpen(true);
                return;
            }

            if (loginResult.data && loginResult.data.user) {
                await handleAuthSuccess(loginResult.data);
            }
        } catch (e) {
            // error already toasted by invokeApi
        } finally {
            setLoading(false);
        }
    };

    /** User selected a workspace in the org picker */
    const handleOrgSelect = async (dq_insider_id, role) => {
        try {
            setOrgSelecting(true);
            const result = await selectOrgService(preAuthToken, dq_insider_id, role);
            if (result.data && result.data.user) {
                setOrgPickerOpen(false);
                await handleAuthSuccess(result.data);
            }
        } catch (e) {
            // pre_auth_token expired or invalid
            setOrgTokenExpired(true);
        } finally {
            setOrgSelecting(false);
        }
    };

    /** User dismissed the org picker — reset state so they can re-login */
    const handleOrgPickerCancel = () => {
        setOrgPickerOpen(false);
        setPreAuthToken(null);
        setOrgPickerOrgs([]);
        setOrgTokenExpired(false);
    };

    // Shared confirm handler — routes to the right persist call based on how the dialog was triggered
    const handleConfirmWarning = async () => {
        setShowConfirmDialog(false);
        if (pendingGuestAction === 'google') {
            await handleGuestGoogleSignIn();
        } else {
            // 'email' path — persist via email + password
            try {
                setLoading(true);
                const result = await persistGuestLoginUser(email.trim(), password, false);

                // v1.2 — guest persist merge can also trigger org picker
                if (result.requires_org_selection) {
                    setPreAuthToken(result.data.pre_auth_token);
                    setOrgPickerOrgs(result.data.orgs || []);
                    setOrgTokenExpired(false);
                    setOrgPickerOpen(true);
                    setPendingGuestAction(null);
                    return;
                }

                if (result.data && result.data.user) {
                    await handleAuthSuccess(result.data);
                }
            } catch (e) {
                // error already toasted by invokeApi
            } finally {
                setLoading(false);
            }
        }
        setPendingGuestAction(null);
    };

    // User cancelled the warning dialog — send them back to their in-progress program
    const handleDialogCancel = () => {
        setShowConfirmDialog(false);
        setPendingGuestAction(null);
        history.push('/dq_pro/program/creation/preview', { userMessage });
    };

    const handleLogoClick = () => {
        const target = process.env.REACT_APP_PBDQ_WEBSITE;
        if (target) {
            window.location.href = target;
        } else {
            history.push('/');
        }
    };

    return (
        <Box className={classes.loginPage}>
            <Box className={classes.container}>
                <Typography className={classes.welcomeText}>Welcome to</Typography>
                <img 
                    src={PoweredByDQ_Logo} 
                    alt="Powered by DQ" 
                    className={classes.logo} 
                    style={{ cursor: 'pointer' }}
                    onClick={handleLogoClick}
                />

                {process.env.REACT_APP_ENABLE_LOGIN_WITH_GOOGLE === '1' && (
                    <Button className={classes.googleButton} onClick={loginWithGoogleAction} disabled={loading}>
                        <img
                            src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png"
                            alt="Google Icon"
                            style={{ width: '20px', height: '20px' }}
                        />
                        Log in with Google
                    </Button>
                )}

                <Box className={classes.dividerContainer}>
                    <Box className={classes.dividerLine}></Box>
                    <Box className={classes.dividerText}>OR</Box>
                    <Box className={classes.dividerLine}></Box>
                </Box>

                <form className={classes.form} onSubmit={(e) => { e.preventDefault(); handleContinue(); }}>
                    <Typography className={classes.label}>Email</Typography>
                    <input
                        className={classes.input}
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loading}
                    />
                    <Typography className={classes.label}>Password</Typography>
                    <input
                        className={classes.input}
                        type="password"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                    />
                    <Box sx={{ textAlign: 'right', mt: -0.5, mb: 1 }}>
                        <Typography 
                            onClick={() => history.push('/reset-password')}
                            sx={{ 
                                fontSize: '14px', 
                                color: 'var(--brand-text-muted, #666)',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                                fontFamily: "'Roboto', sans-serif"
                            }}
                        >
                            Forgot your password?
                        </Typography>
                    </Box>
                    <Button
                        className={classes.continueButton}
                        type="submit"
                        disabled={loading || !email.trim() || !password.trim()}
                    >
                        {loading ? 'Please wait...' : 'Continue'}
                    </Button>
                </form>

                <Typography className={classes.signupText}>
                    Don't have an account? <span className={classes.signupLink} onClick={() => history.push({ pathname: '/signup', referer: location.referer }, { userMessage })}>Sign up</span>
                </Typography>
            </Box>

            <Box className={classes.footerContainer}>
                <Typography className={classes.footerText}>
                    <span className={classes.footerLink}>Terms of Service</span> and <span className={classes.footerLink}>Privacy Policy</span>.
                </Typography>
            </Box>
            <LoaderWithBackDrop loading={loading} />

            <OrgPickerDialog
                open={orgPickerOpen}
                orgs={orgPickerOrgs}
                loading={orgSelecting}
                expired={orgTokenExpired}
                onSelect={handleOrgSelect}
                onCancel={handleOrgPickerCancel}
            />

            {/* ── Guest-session warning dialog ──────────────────────────── */}
            <Dialog
                open={showConfirmDialog}
                onClose={handleDialogCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ style: { borderRadius: 14, padding: '8px 4px', backgroundColor: 'var(--brand-surface, #fff)' } }}
            >
                <DialogTitle sx={{ fontFamily: "'Roboto', sans-serif", fontWeight: 700, fontSize: '20px' }}>
                    Heads up!
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: "'Roboto', sans-serif", color: 'var(--brand-text, #333)', lineHeight: 1.6 }}>
                        Logging into an existing account will merge your guest session into it.{' '}
                        <strong>Your current program in progress may be lost.</strong>
                        <br /><br />
                        If you want to keep it, cancel and save your work first.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: '8px 20px 16px', gap: '8px' }}>
                    <Button
                        onClick={handleDialogCancel}
                        sx={{
                            textTransform: 'none',
                            fontFamily: "'Roboto', sans-serif",
                            color: 'var(--brand-text-muted, #555)',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: 'var(--brand-surface, #f5f5f5)' },
                        }}
                    >
                        Cancel — Keep My Program
                    </Button>
                    <Button
                        onClick={handleConfirmWarning}
                        variant="contained"
                        disableElevation
                        sx={{
                            textTransform: 'none',
                            fontFamily: "'Roboto', sans-serif",
                            backgroundColor: '#0397a6',
                            fontWeight: 700,
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: '#027a87' },
                        }}
                    >
                        Yes, Log In
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DQProV2Login;
